import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/gestionale/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/gestionale/components/ui/card";
import { Badge } from "@/gestionale/components/ui/badge";
import { Button } from "@/gestionale/components/ui/button";
import { Checkbox } from "@/gestionale/components/ui/checkbox";
import { Input } from "@/gestionale/components/ui/input";
import { Textarea } from "@/gestionale/components/ui/textarea";
import { Label } from "@/gestionale/components/ui/label";
import { Plus, ChevronDown, ChevronUp, Trophy, Trash2, Pencil } from "lucide-react";
import { format, parseISO } from "date-fns";
import { useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/gestionale/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/gestionale/components/ui/select";

export default function Partite() {
  const queryClient = useQueryClient();
  const [partitaAperta, setPartitaAperta] = useState<string | null>(null);
  const [showPartitaDialog, setShowPartitaDialog] = useState(false);
  const [showModificaDialog, setShowModificaDialog] = useState(false);
  const [partitaInModifica, setPartitaInModifica] = useState<any | null>(null);
  const [partitaData, setPartitaData] = useState(format(new Date(), "yyyy-MM-dd"));
  const [partitaAvversario, setPartitaAvversario] = useState("");
  const [partitaCasaTrasferta, setPartitaCasaTrasferta] = useState("Casa");
  const [partitaTipo, setPartitaTipo] = useState("Campionato");
  const [partitaGolFatti, setPartitaGolFatti] = useState(0);
  const [partitaGolSubiti, setPartitaGolSubiti] = useState(0);
  const [partitaNote, setPartitaNote] = useState("");

  const { data: giocatori = [] } = useQuery({
    queryKey: ["admin-partite-giocatori"],
    queryFn: async () => {
      const { data: ruoliData, error: ruoliError } = await supabase
        .from("ruoli")
        .select("persona_id")
        .eq("tipo_ruolo", "Giocatore");
      if (ruoliError) throw ruoliError;
      const ids = (ruoliData || []).map((r: any) => r.persona_id);
      if (ids.length === 0) return [];
      const { data, error } = await supabase
        .from("persone")
        .select("id, nome, cognome")
        .in("id", ids)
        .order("cognome");
      if (error) throw error;
      return data || [];
    },
  });

  const { data: partite = [] } = useQuery({
    queryKey: ["admin-partite"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("partite")
        .select("*")
        .order("data", { ascending: false });
      if (error) throw error;
      return data || [];
    },
  });

  const partiteIds = partite.map((p: any) => p.id);

  const { data: statistiche = [] } = useQuery({
    queryKey: ["admin-statistiche-partite", partiteIds.join(",")],
    enabled: partiteIds.length > 0,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("statistiche_partita")
        .select("*")
        .in("partita_id", partiteIds);
      if (error) throw error;
      return data || [];
    },
  });

  const creaPartitaMutation = useMutation({
    mutationFn: async () => {
      const { data: newPartita, error } = await supabase
        .from("partite")
        .insert({
          data: partitaData,
          avversario: partitaAvversario,
          casa_trasferta: partitaCasaTrasferta,
          tipo: partitaTipo,
          gol_fatti: partitaGolFatti,
          gol_subiti: partitaGolSubiti,
          note: partitaNote || null,
        })
        .select()
        .single();
      if (error) throw error;

      if (giocatori.length > 0) {
        const { error: statError } = await supabase
          .from("statistiche_partita")
          .insert(giocatori.map((g: any) => ({ partita_id: newPartita.id, persona_id: g.id })));
        if (statError) throw statError;
      }

      return newPartita;
    },
    onSuccess: (newPartita) => {
      queryClient.invalidateQueries({ queryKey: ["admin-partite"] });
      toast.success("Partita creata");
      setShowPartitaDialog(false);
      setPartitaAvversario("");
      setPartitaGolFatti(0);
      setPartitaGolSubiti(0);
      setPartitaNote("");
      setPartitaAperta(newPartita.id);
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  const modificaPartitaMutation = useMutation({
    mutationFn: async () => {
      if (!partitaInModifica) throw new Error("Nessuna partita selezionata");
      const { error } = await supabase
        .from("partite")
        .update({
          data: partitaInModifica.data,
          avversario: partitaInModifica.avversario,
          casa_trasferta: partitaInModifica.casa_trasferta,
          tipo: partitaInModifica.tipo,
          gol_fatti: partitaInModifica.gol_fatti,
          gol_subiti: partitaInModifica.gol_subiti,
          note: partitaInModifica.note || null,
        })
        .eq("id", partitaInModifica.id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-partite"] });
      toast.success("Partita modificata");
      setShowModificaDialog(false);
      setPartitaInModifica(null);
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  const eliminaPartitaMutation = useMutation({
    mutationFn: async (partitaId: string) => {
      const { error: statError } = await supabase
        .from("statistiche_partita")
        .delete()
        .eq("partita_id", partitaId);
      if (statError) throw statError;

      const { error } = await supabase.from("partite").delete().eq("id", partitaId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-partite"] });
      queryClient.invalidateQueries({ queryKey: ["admin-statistiche-partite"] });
      toast.success("Partita eliminata");
      setPartitaAperta(null);
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  const updateStatisticaMutation = useMutation({
    mutationFn: async ({
      statId,
      field,
      value,
    }: {
      statId: string;
      field: string;
      value: number | boolean;
    }) => {
      const { error } = await supabase
        .from("statistiche_partita")
        .update({ [field]: value })
        .eq("id", statId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-statistiche-partite"] });
    },
    onError: (e: any) => toast.error("Errore: " + e.message),
  });

  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-3xl font-bold tracking-tight">Partite</h1>
        <p className="text-muted-foreground mt-1">
          Inserisci i risultati e le statistiche individuali dei giocatori.
        </p>
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2">
              <Trophy className="h-4 w-4 text-primary" />
              Partite <Badge variant="outline">{partite.length}</Badge>
            </CardTitle>
            <Button size="sm" onClick={() => setShowPartitaDialog(true)}>
              <Plus className="h-4 w-4 mr-1" /> Nuova partita
            </Button>
          </div>
        </CardHeader>

        <CardContent className="space-y-2">
          {partite.length === 0 ? (
            <p className="text-sm text-muted-foreground">Nessuna partita registrata.</p>
          ) : (
            partite.map((p: any) => {
              const isOpen = partitaAperta === p.id;
              const statPartita = statistiche.filter((s: any) => s.partita_id === p.id);
              const risultato = `${p.gol_fatti} - ${p.gol_subiti}`;

              return (
                <div key={p.id} className="rounded-lg border">
                  <div className="flex items-center">
                    <button
                      className="flex-1 flex items-center justify-between p-3 text-sm hover:bg-muted/50 transition-colors"
                      onClick={() => setPartitaAperta(isOpen ? null : p.id)}
                    >
                      <div className="flex items-center gap-3">
                        <span className="font-medium">{format(parseISO(p.data), "dd/MM/yyyy")}</span>
                        <span>{p.casa_trasferta} vs {p.avversario}</span>
                        <Badge variant="outline" className="text-xs">{p.tipo}</Badge>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge
                          variant="outline"
                          className={`text-xs font-bold ${p.gol_fatti > p.gol_subiti ? "border-green-500 text-green-700" : p.gol_fatti < p.gol_subiti ? "border-red-500 text-red-700" : ""}`}
                        >
                          {risultato}
                        </Badge>
                        {isOpen ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                      </div>
                    </button>

                    <button
                      className="p-3 text-muted-foreground hover:text-primary hover:bg-muted/50 transition-colors"
                      title="Modifica partita"
                      onClick={(e) => {
                        e.stopPropagation();
                        setPartitaInModifica({ ...p });
                        setShowModificaDialog(true);
                      }}
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      className="p-3 text-destructive hover:bg-destructive/10 transition-colors"
                      title="Elimina partita"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (confirm("Eliminare questa partita?")) eliminaPartitaMutation.mutate(p.id);
                      }}
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>

                  {isOpen && (
                    <div className="border-t p-3">
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs">
                          <thead>
                            <tr className="text-muted-foreground">
                              <th className="text-left p-2">Giocatore</th>
                              <th className="text-center p-2">Titolare</th>
                              <th className="text-center p-2">Min</th>
                              <th className="text-center p-2">⚽</th>
                              <th className="text-center p-2">🟨</th>
                              <th className="text-center p-2">🟥</th>
                            </tr>
                          </thead>
                          <tbody>
                            {giocatori.map((g: any) => {
                              const stat = statPartita.find((s: any) => s.persona_id === g.id);
                              if (!stat) return null;

                              return (
                                <tr key={g.id} className="border-t hover:bg-muted/30">
                                  <td className="p-2 font-medium">{g.cognome} {g.nome}</td>
                                  <td className="p-2 text-center">
                                    <Checkbox
                                      checked={stat.titolare}
                                      onCheckedChange={(v) =>
                                        updateStatisticaMutation.mutate({
                                          statId: stat.id,
                                          field: "titolare",
                                          value: !!v,
                                        })
                                      }
                                    />
                                  </td>
                                  <td className="p-2 text-center">
                                    <input
                                      type="number"
                                      min={0}
                                      max={120}
                                      className="w-12 text-center border rounded p-1 bg-background text-xs"
                                      defaultValue={stat.minuti_giocati}
                                      onBlur={(e) =>
                                        updateStatisticaMutation.mutate({
                                          statId: stat.id,
                                          field: "minuti_giocati",
                                          value: parseInt(e.target.value) || 0,
                                        })
                                      }
                                    />
                                  </td>
                                  <td className="p-2 text-center">
                                    <input
                                      type="number"
                                      min={0}
                                      className="w-10 text-center border rounded p-1 bg-background text-xs"
                                      defaultValue={stat.goal}
                                      onBlur={(e) =>
                                        updateStatisticaMutation.mutate({
                                          statId: stat.id,
                                          field: "goal",
                                          value: parseInt(e.target.value) || 0,
                                        })
                                      }
                                    />
                                  </td>
                                  <td className="p-2 text-center">
                                    <input
                                      type="number"
                                      min={0}
                                      max={2}
                                      className="w-10 text-center border rounded p-1 bg-background text-xs"
                                      defaultValue={stat.ammonizioni}
                                      onBlur={(e) =>
                                        updateStatisticaMutation.mutate({
                                          statId: stat.id,
                                          field: "ammonizioni",
                                          value: parseInt(e.target.value) || 0,
                                        })
                                      }
                                    />
                                  </td>
                                  <td className="p-2 text-center">
                                    <input
                                      type="number"
                                      min={0}
                                      max={1}
                                      className="w-10 text-center border rounded p-1 bg-background text-xs"
                                      defaultValue={stat.espulsioni}
                                      onBlur={(e) =>
                                        updateStatisticaMutation.mutate({
                                          statId: stat.id,
                                          field: "espulsioni",
                                          value: parseInt(e.target.value) || 0,
                                        })
                                      }
                                    />
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </CardContent>
      </Card>

      <Dialog
        open={showModificaDialog}
        onOpenChange={(open) => {
          if (!open) {
            setShowModificaDialog(false);
            setPartitaInModifica(null);
          }
        }}
      >
        <DialogContent>
          <DialogHeader><DialogTitle>Modifica Partita</DialogTitle></DialogHeader>
          {partitaInModifica && (
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Data *</Label>
                <Input
                  type="date"
                  value={partitaInModifica.data}
                  onChange={(e) => setPartitaInModifica({ ...partitaInModifica, data: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>Avversario *</Label>
                <Input
                  value={partitaInModifica.avversario}
                  onChange={(e) => setPartitaInModifica({ ...partitaInModifica, avversario: e.target.value })}
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Casa/Trasferta</Label>
                  <Select
                    value={partitaInModifica.casa_trasferta}
                    onValueChange={(value) => setPartitaInModifica({ ...partitaInModifica, casa_trasferta: value })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Casa">Casa</SelectItem>
                      <SelectItem value="Trasferta">Trasferta</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Tipo</Label>
                  <Select
                    value={partitaInModifica.tipo}
                    onValueChange={(value) => setPartitaInModifica({ ...partitaInModifica, tipo: value })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="Campionato">Campionato</SelectItem>
                      <SelectItem value="Coppa">Coppa</SelectItem>
                      <SelectItem value="Amichevole">Amichevole</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Gol fatti</Label>
                  <Input
                    type="number"
                    min={0}
                    value={partitaInModifica.gol_fatti}
                    onChange={(e) => setPartitaInModifica({ ...partitaInModifica, gol_fatti: parseInt(e.target.value) || 0 })}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Gol subiti</Label>
                  <Input
                    type="number"
                    min={0}
                    value={partitaInModifica.gol_subiti}
                    onChange={(e) => setPartitaInModifica({ ...partitaInModifica, gol_subiti: parseInt(e.target.value) || 0 })}
                  />
                </div>
              </div>
              <div className="space-y-2">
                <Label>Note</Label>
                <Textarea
                  value={partitaInModifica.note || ""}
                  onChange={(e) => setPartitaInModifica({ ...partitaInModifica, note: e.target.value })}
                  placeholder="Inserisci una cronaca o altre informazioni sulla partita"
                  rows={5}
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModificaDialog(false)}>Annulla</Button>
            <Button
              onClick={() => modificaPartitaMutation.mutate()}
              disabled={modificaPartitaMutation.isPending || !partitaInModifica?.data || !partitaInModifica?.avversario}
            >
              Salva modifiche
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showPartitaDialog}
        onOpenChange={(open) => {
          if (!open) setShowPartitaDialog(false);
        }}
      >
        <DialogContent>
          <DialogHeader><DialogTitle>Nuova Partita</DialogTitle></DialogHeader>
          <div className="space-y-4">
            <div className="space-y-2">
              <Label>Data *</Label>
              <Input type="date" value={partitaData} onChange={(e) => setPartitaData(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label>Avversario *</Label>
              <Input
                value={partitaAvversario}
                onChange={(e) => setPartitaAvversario(e.target.value)}
                placeholder="Es. A.S.D. Rivale"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Casa/Trasferta</Label>
                <Select value={partitaCasaTrasferta} onValueChange={setPartitaCasaTrasferta}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Casa">Casa</SelectItem>
                    <SelectItem value="Trasferta">Trasferta</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo</Label>
                <Select value={partitaTipo} onValueChange={setPartitaTipo}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Campionato">Campionato</SelectItem>
                    <SelectItem value="Coppa">Coppa</SelectItem>
                    <SelectItem value="Amichevole">Amichevole</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Gol fatti</Label>
                <Input type="number" min={0} value={partitaGolFatti} onChange={(e) => setPartitaGolFatti(parseInt(e.target.value) || 0)} />
              </div>
              <div className="space-y-2">
                <Label>Gol subiti</Label>
                <Input type="number" min={0} value={partitaGolSubiti} onChange={(e) => setPartitaGolSubiti(parseInt(e.target.value) || 0)} />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Note</Label>
              <Textarea
                value={partitaNote}
                onChange={(e) => setPartitaNote(e.target.value)}
                placeholder="Opzionale"
                rows={4}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPartitaDialog(false)}>Annulla</Button>
            <Button
              onClick={() => creaPartitaMutation.mutate()}
              disabled={creaPartitaMutation.isPending || !partitaData || !partitaAvversario}
            >
              Crea
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
